import { ArrowDown2 } from "iconsax-reactjs";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { ActivityRecord, getActivities } from "../../../shared/api/activities";
import { getApiErrorMessage } from "../../../shared/lib";
import {
  BodyText,
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";

type KybComplianceActivityLogModalProps = {
  open: boolean;
  businessId: string;
  customerName: string;
  onOpenChange: (open: boolean) => void;
};

const formatActivityTimestamp = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const datePart = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  return `${datePart} • ${timePart}`;
};

const getActivityInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "A";

const KybComplianceActivityLogModal = ({
  open,
  businessId,
  onOpenChange,
}: KybComplianceActivityLogModalProps) => {
  const [activities, setActivities] = useState<ActivityRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !businessId) return;

    let isMounted = true;

    const fetchActivities = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await getActivities({ entityId: businessId });
        if (!isMounted) return;
        setActivities(result.items);
      } catch (fetchError) {
        if (!isMounted) return;
        setActivities([]);
        setError(getApiErrorMessage(fetchError, "Unable to load activity log"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchActivities();

    return () => {
      isMounted = false;
    };
  }, [businessId, open]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[560px] bg-white"
    >
      <ModalHeader className="items-center">
        <ModalTitle className="aku-heading-h2-semibold">
          Activity log
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          onClick={() => onOpenChange(false)}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF]"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <ModalBody className="pt-4">
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-full bg-[#F3F4FB] px-4 py-2 aku-body-small-regular text-[#383B56]"
        >
          <span>
            Action:{" "}
            <span className="aku-body-small-medium text-[#161827]">All</span>
          </span>
          <ArrowDown2 size={16} className="text-[#6F739B]" />
        </button>

        <div className="mt-6 rounded-[12px] border border-[#E1E6FF] p-6">
          {isLoading ? (
            <BodyText className="aku-body-small-regular text-[#6F739B]">
              Loading activity log...
            </BodyText>
          ) : error ? (
            <BodyText className="aku-body-small-regular text-[#C0392B]">
              {error}
            </BodyText>
          ) : activities.length === 0 ? (
            <BodyText className="aku-body-small-regular text-[#6F739B]">
              No activity has been recorded for this business yet.
            </BodyText>
          ) : (
            <div className="relative space-y-8 before:absolute before:left-[10px] before:top-3 before:h-[calc(100%-14px)] before:w-px before:bg-[#E1E6FF]">
              {activities.map((activity) => (
                <div key={activity.id} className="relative pl-8">
                  <span className="absolute left-[5px] top-[28px] z-[1] h-3 w-3 rounded-full bg-[#DDE2FA]" />
                  <BodyText className="aku-label-regular text-[#6F739B]">
                    {formatActivityTimestamp(activity.timestamp)}
                  </BodyText>
                  <div className="mt-2 flex items-center gap-3">
                    {activity.memberProfileImage ? (
                      <img
                        src={activity.memberProfileImage}
                        alt={activity.memberName}
                        className="h-5 w-5 shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#EEE8FF] text-[11px] font-[500] text-[#6F00FF]">
                        {getActivityInitials(activity.memberName)}
                      </span>
                    )}
                    <div className="aku-body-small-regular text-[#383B56]">
                      {activity.description || "-"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default KybComplianceActivityLogModal;
